import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Search, Eye, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string | null;
  message: string;
  preferred_contact: string | null;
  status: string;
  rejection_reasons: string[] | null;
  ip_address: string | null;
  user_agent: string | null;
  form_completion_time_seconds: number | null;
  turnstile_token_valid: boolean | null;
  submitted_at: string;
  verified_at: string | null;
  email_sent_to_4fox_at: string | null;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending_verification: { label: "Pending", color: "bg-yellow-500", icon: <Clock className="h-3 w-3" /> },
  verified: { label: "Verified", color: "bg-green-500", icon: <CheckCircle2 className="h-3 w-3" /> },
  blocked_spam: { label: "Spam", color: "bg-red-500", icon: <XCircle className="h-3 w-3" /> },
  rejected_bot: { label: "Bot", color: "bg-red-500", icon: <XCircle className="h-3 w-3" /> },
  rate_limited: { label: "Rate Limited", color: "bg-orange-500", icon: <AlertTriangle className="h-3 w-3" /> },
};

export const ContactSubmissionsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: submissions = [], isLoading, refetch } = useQuery({
    queryKey: ["contact-submissions", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("contact_submissions")
        .select("*")
        .order("submitted_at", { ascending: false });

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ContactSubmission[];
    },
  });

  const filteredSubmissions = submissions.filter(
    (sub) =>
      sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sub.company && sub.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleViewDetails = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || { label: status, color: "bg-gray-500", icon: null };
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email, name, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending_verification">Pending</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="blocked_spam">Spam</SelectItem>
              <SelectItem value="rejected_bot">Bot</SelectItem>
              <SelectItem value="rate_limited">Rate Limited</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSubmissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No submissions found
                </TableCell>
              </TableRow>
            ) : (
              filteredSubmissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell className="font-medium">{submission.name}</TableCell>
                  <TableCell>{submission.email}</TableCell>
                  <TableCell>{getStatusBadge(submission.status)}</TableCell>
                  <TableCell>{format(new Date(submission.submitted_at), "dd/MM/yy HH:mm")}</TableCell>
                  <TableCell>
                    {submission.verified_at
                      ? format(new Date(submission.verified_at), "dd/MM/yy HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleViewDetails(submission)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
            <DialogDescription>
              Full details of the contact form submission
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Name</p>
                  <p>{selectedSubmission.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <p>{selectedSubmission.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Phone</p>
                  <p>{selectedSubmission.phone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Company</p>
                  <p>{selectedSubmission.company || "-"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Preferred Contact</p>
                  <p className="capitalize">{selectedSubmission.preferred_contact || "-"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  {getStatusBadge(selectedSubmission.status)}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Message</p>
                <div className="bg-muted p-3 rounded-lg whitespace-pre-wrap text-sm">
                  {selectedSubmission.message}
                </div>
              </div>

              {selectedSubmission.rejection_reasons && selectedSubmission.rejection_reasons.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Rejection Reasons</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedSubmission.rejection_reasons.map((reason, idx) => (
                      <Badge key={idx} variant="destructive" className="text-xs">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-3">Security Info</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">IP Address</p>
                    <p className="font-mono">{selectedSubmission.ip_address || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Form Time</p>
                    <p>{selectedSubmission.form_completion_time_seconds?.toFixed(1) || "-"}s</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Turnstile Valid</p>
                    <p>{selectedSubmission.turnstile_token_valid ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">User Agent</p>
                    <p className="text-xs truncate" title={selectedSubmission.user_agent || ""}>
                      {selectedSubmission.user_agent || "-"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-3">Timeline</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{format(new Date(selectedSubmission.submitted_at), "dd/MM/yyyy HH:mm:ss")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified</span>
                    <span>
                      {selectedSubmission.verified_at
                        ? format(new Date(selectedSubmission.verified_at), "dd/MM/yyyy HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email Sent to 4FOX</span>
                    <span>
                      {selectedSubmission.email_sent_to_4fox_at
                        ? format(new Date(selectedSubmission.email_sent_to_4fox_at), "dd/MM/yyyy HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
